import { Transition } from '@headlessui/react'
import {
  cloneElement,
  createElement,
  useCallback,
  useRef,
  useState,
} from 'react'
import { usePopper } from 'react-popper'
import { tm } from '../tm'
import type { Placement } from '@popperjs/core'
import type { ReactNode } from 'react'

export type TooltipPlacement = Placement

type TooltipProps = {
  children: JSX.Element | string
  disabled?: boolean
  enterDelay?: number
  id: string
  label: ReactNode
  leaveDelay?: number
  placement?: TooltipPlacement
  popperClassName?: string
  tabIndex?: number
  clonedTriggerElementClassName?: string
}

function createTriggerElement(children: JSX.Element | string) {
  if (typeof children === 'string') {
    return createElement('div', null, children)
  }

  return <div>{children}</div>
}

export function Tooltip({
  children,
  disabled = false,
  enterDelay = 0,
  id,
  label,
  leaveDelay = 0,
  placement = 'bottom',
  popperClassName = '',
  clonedTriggerElementClassName = '',
  tabIndex = 0,
}: TooltipProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [popperRef, setPopperRef] = useState<HTMLElement | null>(null)
  const [triggerElementRef, setTriggerElementRef] =
    useState<HTMLElement | null>(null)

  const { styles, attributes } = usePopper(triggerElementRef, popperRef, {
    placement,
    modifiers: [
      { name: 'offset', options: { offset: [0, 8] } },
      {
        name: 'flip',
        options: {
          padding: 70,
        },
      },
    ],
  })

  const enterTimeout = useRef<NodeJS.Timeout>()
  const leaveTimeout = useRef<NodeJS.Timeout>()

  const enter = useCallback(() => {
    if (leaveTimeout.current) clearTimeout(leaveTimeout.current)
    enterTimeout.current = setTimeout(() => setIsOpen(true), enterDelay)
  }, [enterDelay])

  const leave = useCallback(() => {
    if (enterTimeout.current) clearTimeout(enterTimeout.current)
    leaveTimeout.current = setTimeout(() => setIsOpen(false), leaveDelay)
  }, [leaveDelay])

  if (!label) return children
  if (disabled) return children

  const enhancedId = `tp-${id}`

  const triggerElement = createTriggerElement(children)
  const clonedTriggerElement = cloneElement(triggerElement, {
    'aria-describedby': enhancedId,
    ref: setTriggerElementRef,
    onMouseEnter: enter,
    tabIndex,
    children: triggerElement.props.children,
    onMouseLeave: leave,
    onFocus: enter,
    onBlur: leave,
    className: tm(
      'relative focus-visible:outline-dashed outline-current rounded-md hover:cursor-pointer',
      clonedTriggerElementClassName,
    ),
  })

  return (
    <>
      {clonedTriggerElement}
      <Transition
        as="div"
        show={isOpen}
        id={enhancedId}
        ref={setPopperRef}
        style={styles.popper}
        {...attributes.popper}
        className={tm(
          'pointer-events-none z-[99] max-w-[300px] rounded-lg bg-light-black p-3 text-sm text-white shadow-lg transition-opacity duration-300 ease-out data-[closed]:opacity-0',
          popperClassName,
        )}
        unmount
      >
        {label}
      </Transition>
    </>
  )
}
